// frontend/src/components/homepage/ProseSection.js
//
// The live-mesh page's section of PURE WORDS: eyebrow, H2, one or more body
// paragraphs, and an optional slot underneath for whatever the caller wants to
// put there.
//
// WHY IT EXISTS. Every homepage section owns its own copy as a module constant
// and renders a bespoke body - which is right for the homepage, where each
// section is a different machine. A profession landing page needs the opposite:
// the same three typographic moves with different words, written once in a
// content module. This is that shell, and it is the head block the other two
// profession section (OpportunitySection) composes with, so
// the page can never grow two spellings of the same heading grammar.
//
// IT IS THE HOMEPAGE'S GRAMMAR, NOT A NEW ONE. `SectionShell` for the rhythm,
// the mesh/scene data attributes and the live gate; `hl-eyebrow` / `hl-h2` /
// `hl-sub` for the type; ONE `useReveal` over the whole head, exactly as
// TypesLive does - the eyebrow, the heading and the prose arrive together
// because they are one thought. The sheet this file adds (professionLanding.css)
// carries four rules and every selector is `.hl-pl-` prefixed; homepage.css is
// not touched.
//
// PRERENDER SHAPE. `useReveal` disarms itself under `window.__PRERENDER__` and
// under motion-off, so the class it returns is "" there and the snapshot carries
// no `opacity: 0`. Every text element takes exactly ONE expression child, so
// each renders a single text node and the captured HTML hydrates cleanly - two
// adjacent JSX text nodes are the React 19 root-regeneration trap this page has
// already been bitten by.
//
// ONE H2 PER SECTION, and the h2 is unconditional: a section with no heading is
// a section a crawler cannot place, so `title` is required rather than guarded.

import React from "react";
import SectionShell from "./SectionShell";
import useReveal from "../../motion/useReveal";
import "../../professionLanding.css";

/**
 * Normalise the `body` prop to a list of non-empty paragraphs.
 *
 * Exported because it is the boundary between a content module (which may write
 * a single string or an array, and may leave a trailing empty entry behind an
 * edit) and the DOM: an empty `<p>` is 0 characters of section text, and the
 * prerender's per-section text floor counts characters, not elements.
 *
 * @param {string|string[]|undefined} body
 * @returns {string[]}
 */
export function toParagraphs(body) {
  if (!body) return [];
  const list = Array.isArray(body) ? body : [body];
  return list.map((entry) => (typeof entry === "string" ? entry.trim() : "")).filter(Boolean);
}

/**
 * The eyebrow / H2 / prose block, revealed as one unit.
 *
 * Split out so OpportunitySection gets the identical head
 * by composing this file rather than by copying six lines of class names.
 *
 * @param {object} props
 * @param {string} [props.eyebrow] the small tracked mono line above the heading.
 * @param {string} props.title the section's H2 - the only H2 it may have.
 * @param {string|string[]} [props.body] one or more body paragraphs.
 * @param {string} [props.className] extra classes for the head box.
 */
export function ProseHead({ eyebrow, title, body, className }) {
  const head = useReveal();
  const paragraphs = toParagraphs(body);
  // The head lays out SIDE BY SIDE above 900px whenever it has prose to put
  // beside the title, and stacks when it does not. That is the whole rule -
  // there is no prop, because "split when there is a body" is not a per-section
  // taste: a lone title in a 5fr column would just be a narrower title.
  const classes = [
    "hl-head",
    "hl-pl-head",
    paragraphs.length > 0 ? "hl-pl-head-split" : null,
    className,
    head.className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div ref={head.ref} className={classes}>
      {eyebrow ? <div className="hl-eyebrow">{eyebrow}</div> : null}
      <h2 className="hl-h2">{title}</h2>
      {paragraphs.length > 0 && (
        // The wrapper is what the split grid places in the second column, and
        // it is also the body's measure box. It exists even when stacked, where
        // it is an ordinary block and changes nothing.
        <div className="hl-pl-head-body">
          {paragraphs.map((text, index) => (
            // The key is the index because the list is static content-module
            // data that never reorders; the paragraph text itself would be a
            // longer key for no reordering to protect against.
            <p key={`hl-pl-p-${index}`} className="hl-sub hl-pl-p">
              {text}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {string} [props.id] anchor id - also the section's handle in the
 *   page's prerender section contract, so a content module that names a section
 *   must pass the same id here.
 * @param {string} [props.eyebrow]
 * @param {string} props.title
 * @param {string|string[]} [props.body]
 * @param {string} [props.scene] SceneWashes key. Defaults to the features wash;
 *   an unknown key simply keeps the current scene, it can never blank the page.
 * @param {string} [props.mesh] mesh formation: cloud | pipeline | core | disperse.
 * @param {string|number} [props.meshAlpha]
 * @param {string} [props.className] extra classes for the <section>.
 * @param {string} [props.headClassName] extra classes for the head box.
 * @param {object} [props.sx] extra styles for the <section>.
 * @param {React.ReactNode} [props.children] the section's body, under the head.
 */
export default function ProseSection({
  id,
  eyebrow,
  title,
  body,
  scene = "features",
  mesh = "disperse",
  meshAlpha = ".25",
  className,
  headClassName,
  sx,
  children,
}) {
  return (
    <SectionShell
      id={id}
      scene={scene}
      mesh={mesh}
      meshAlpha={meshAlpha}
      className={className ? `hl-pl-sec ${className}` : "hl-pl-sec"}
      sx={sx}
    >
      <ProseHead eyebrow={eyebrow} title={title} body={body} className={headClassName} />
      {children}
    </SectionShell>
  );
}
